/**
 * /workspace/marketing/{sms,whatsapp,broadcast} — Twilio composer.
 *
 * Single component parameterised by `channel`. Sends real SMS or WhatsApp
 * messages via the Twilio integration. Requires super-admin.
 */
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Send, Smartphone, MessageCircle, Megaphone, ExternalLink, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

type Channel = "sms" | "whatsapp" | "broadcast";

interface TwilioStatus {
  sms_ready: boolean;
  whatsapp_ready: boolean;
  verify_ready: boolean;
  from_number: string | null;
  whatsapp_from: string | null;
}

const HERO: Record<Channel, { title: string; subtitle: string; icon: React.ComponentType<any>; tone: string }> = {
  sms:       { title: "Bulk SMS",              subtitle: "Send live SMS via Twilio to any E.164 phone number.",             icon: Smartphone,    tone: "from-blue-600 via-fuchsia-700 to-[color:var(--sb-saffron,#F97316)]" },
  whatsapp:  { title: "WhatsApp Marketing",    subtitle: "Send live WhatsApp messages via Twilio (sandbox or verified sender).", icon: MessageCircle, tone: "from-emerald-600 via-emerald-700 to-emerald-500" },
  broadcast: { title: "Bulk Broadcasting",     subtitle: "Push one message to hundreds of phones across SMS or WhatsApp.", icon: Megaphone,     tone: "from-fuchsia-700 via-[color:var(--sb-navy,#0A1128)] to-[color:var(--sb-saffron,#F97316)]" },
};


function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
      ok ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
         : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"}`}>
      {ok ? <CheckCircle2 className="h-3 w-3"/> : <AlertCircle className="h-3 w-3"/>} {label}
    </span>
  );
}


export default function TwilioComposer({ channel }: { channel: Channel }) {
  const hero = HERO[channel];
  const qc = useQueryClient();

  const st = useQuery<TwilioStatus>({
    queryKey: ["twilio-status"],
    queryFn: async () => (await api.get("/twilio/status")).data,
    refetchInterval: 30_000,
  });

  const [broadcastChannel, setBroadcastChannel] = React.useState<"sms" | "whatsapp">("sms");
  const activeChan: "sms" | "whatsapp" = channel === "broadcast" ? broadcastChannel : channel;
  const [to, setTo] = React.useState("");
  const [recipients, setRecipients] = React.useState("");
  const [body, setBody] = React.useState("");
  const [schedule, setSchedule] = React.useState<{ enabled: boolean; when: string }>({ enabled: false, when: "" });

  const canSend = activeChan === "sms" ? st.data?.sms_ready : st.data?.whatsapp_ready;

  const formatErr = (e: any): string => {
    const d = e?.response?.data?.detail ?? e?.message ?? "Send failed";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((it) => it?.msg || JSON.stringify(it)).join(" · ");
    return typeof d === "object" ? (d.msg || JSON.stringify(d)) : String(d);
  };

  const sendSingle = useMutation({
    mutationFn: async () => (await api.post(`/twilio/${activeChan}/send`, { to: to.trim(), body })).data,
    onSuccess: (d) => {
      if (d?.ok) {
        toast.success(`Sent — SID ${d.sid?.slice(0, 12)}… (${d.status})`);
        setTo(""); setBody("");
      } else {
        toast.error(typeof d?.error === "string" ? d.error : "Send failed");
      }
      qc.invalidateQueries({ queryKey: ["twilio-recent"] });
    },
    onError: (e: any) => toast.error(formatErr(e)),
  });

  const sendBroadcast = useMutation({
    mutationFn: async () => {
      const parsed = recipients.split(/[\s,;\n]+/).map((r) => r.trim()).filter(Boolean);
      // Scheduled path — queue for later dispatch.
      if (schedule.enabled && schedule.when) {
        const iso = new Date(schedule.when).toISOString();
        return (await api.post("/twilio/broadcast/schedule", { channel: activeChan, recipients: parsed, body, scheduled_for: iso })).data;
      }
      return (await api.post("/twilio/broadcast", { channel: activeChan, recipients: parsed, body })).data;
    },
    onSuccess: (d: any) => {
      if (d?.id) {
        toast.success(`Broadcast scheduled for ${new Date(d.scheduled_for).toLocaleString()}`);
        setRecipients(""); setBody(""); setSchedule({ enabled: false, when: "" });
        qc.invalidateQueries({ queryKey: ["twilio-scheduled"] });
        return;
      }
      if (d?.ok) {
        toast.success(`Broadcast: ${d.sent}/${d.total} sent · ${d.failed} failed`);
        setRecipients(""); setBody("");
      } else {
        toast.error("Broadcast failed");
      }
      qc.invalidateQueries({ queryKey: ["twilio-recent"] });
    },
    onError: (e: any) => toast.error(formatErr(e)),
  });

  const scheduledList = useQuery<{ items: any[] }>({
    queryKey: ["twilio-scheduled"],
    queryFn: async () => (await api.get("/twilio/broadcast/schedule?status=pending")).data,
    enabled: channel === "broadcast",
    refetchInterval: 30_000,
  });

  const cancelSched = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/twilio/broadcast/schedule/${id}`)).data,
    onSuccess: () => { toast.success("Cancelled"); qc.invalidateQueries({ queryKey: ["twilio-scheduled"] }); },
  });

  const recent = useQuery<{ items: any[] }>({
    queryKey: ["twilio-recent", activeChan],
    queryFn: async () => (await api.get(`/twilio/messages?limit=20&kind=${activeChan}`)).data,
    refetchInterval: 15_000,
  });

  return (
    <DashboardLayout title={hero.title} subtitle={hero.subtitle} seoNoindex>
      {/* Hero */}
      <div className={`relative rounded-2xl overflow-hidden border border-[color:var(--sg-border)] mb-6 bg-gradient-to-br ${hero.tone}`} data-testid="twilio-hero">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 20% 40%, rgba(249,115,22,0.35), transparent 40%), radial-gradient(circle at 80% 60%, rgba(217,70,239,0.3), transparent 40%)" }}/>
        <div className="relative px-6 py-6 text-white">
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-white/70">Powered by Twilio</div>
          <div className="mt-2 font-serif-display text-3xl sm:text-5xl font-bold leading-none">{hero.title}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <StatusPill ok={!!st.data?.sms_ready}      label={`SMS · ${st.data?.from_number || "not set"}`}/>
            <StatusPill ok={!!st.data?.whatsapp_ready} label={`WhatsApp · ${st.data?.whatsapp_from || "not set"}`}/>
            <StatusPill ok={!!st.data?.verify_ready}   label="OTP Verify"/>
          </div>
        </div>
      </div>

      {/* Not-configured banner */}
      {!canSend && st.data && (
        <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-100 flex items-start gap-3" data-testid="twilio-not-configured">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0"/>
          <div className="flex-1">
            <div className="font-semibold">Twilio {activeChan.toUpperCase()} not configured yet.</div>
            <div className="text-xs opacity-80 mt-0.5">Paste your Twilio credentials in Admin Setup → Marketing Providers to send live messages.</div>
          </div>
          <Link to="/admin/setup" className="text-xs font-semibold underline flex items-center gap-1 shrink-0">Open setup <ExternalLink className="h-3 w-3"/></Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Composer */}
        <div className="lg:col-span-2 rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5" data-testid="twilio-composer">
          {channel === "broadcast" && (
            <div className="mb-3">
              <div className="sg-label mb-1.5">Channel</div>
              <div className="inline-flex rounded-full border border-[color:var(--sg-border)] p-0.5 bg-[color:var(--sg-muted-bg)]">
                {(["sms", "whatsapp"] as const).map((c) => (
                  <button
                    key={c}
                    data-testid={`bc-ch-${c}`}
                    onClick={() => setBroadcastChannel(c)}
                    className={`px-3 py-1 text-xs font-semibold rounded-full ${broadcastChannel === c ? "bg-[color:var(--sb-saffron,#F97316)] text-white" : "text-[color:var(--sg-fg)]"}`}
                  >{c === "sms" ? "SMS" : "WhatsApp"}</button>
                ))}
              </div>
            </div>
          )}

          {channel === "broadcast" ? (
            <div>
              <div className="sg-label mb-1.5">Recipients (comma / newline separated · E.164 · max 500)</div>
              <textarea
                data-testid="bc-recipients"
                value={recipients}
                onChange={(e) => setRecipients(e.target.value)}
                rows={4}
                placeholder="+14155551234, +919876543210&#10;+15551239876"
                className="w-full rounded-lg border border-[color:var(--sg-border)] bg-transparent px-3 py-2 text-sm font-mono"
              />
              <div className="text-[11px] text-[color:var(--sg-muted)] mt-1">
                {recipients.split(/[\s,;\n]+/).filter((r) => r.trim()).length} recipient(s) queued.
              </div>
            </div>
          ) : (
            <div>
              <div className="sg-label mb-1.5">To (E.164 phone, e.g. +14155551234)</div>
              <input
                data-testid="tw-to"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="+14155551234"
                className="w-full rounded-lg border border-[color:var(--sg-border)] bg-transparent px-3 py-2 text-sm"
              />
            </div>
          )}

          <div className="mt-4">
            <div className="flex items-center justify-between">
              <div className="sg-label">Message body</div>
              <div className="text-[11px] text-[color:var(--sg-muted)] tabular-nums">{body.length} / 1600</div>
            </div>
            <textarea
              data-testid="tw-body"
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, 1600))}
              rows={5}
              placeholder={activeChan === "whatsapp"
                ? "Hi {{name}} — your SociBaba order shipped! Track: sb.co/o/{{id}}"
                : "SociBaba: Your product 'Turmeric Latte' is trending — grab 30% off today only."}
              className="w-full rounded-lg border border-[color:var(--sg-border)] bg-transparent px-3 py-2 text-sm"
            />
          </div>

          {channel === "broadcast" && (
            <div className="mt-4 rounded-lg border border-dashed border-[color:var(--sg-border)] p-3" data-testid="tw-schedule-box">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="tw-schedule-toggle"
                  type="checkbox"
                  checked={schedule.enabled}
                  onChange={(e) => setSchedule({ ...schedule, enabled: e.target.checked })}
                  className="h-4 w-4"
                />
                <span className="text-sm font-semibold text-[color:var(--sg-fg)]">Schedule for later</span>
              </label>
              {schedule.enabled && (
                <div className="mt-2">
                  <input
                    data-testid="tw-schedule-when"
                    type="datetime-local"
                    value={schedule.when}
                    min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                    onChange={(e) => setSchedule({ ...schedule, when: e.target.value })}
                    className="h-9 rounded-md border border-[color:var(--sg-border)] bg-transparent px-2 text-sm"
                  />
                  <div className="text-[11px] text-[color:var(--sg-muted)] mt-1">Broadcast will fire within 60 seconds of the scheduled time.</div>
                </div>
              )}
            </div>
          )}

          <div className="mt-4 flex items-center justify-between gap-2">
            <div className="text-[11px] text-[color:var(--sg-muted)]">
              {activeChan === "sms" ? "Standard SMS · billed per Twilio segment" : "WhatsApp session or template message"}
            </div>
            <Button
              data-testid="tw-send"
              disabled={!canSend || !body.trim() || (channel === "broadcast" ? !recipients.trim() : !to.trim()) || (schedule.enabled && !schedule.when) || sendSingle.isPending || sendBroadcast.isPending}
              onClick={() => (channel === "broadcast" ? sendBroadcast.mutate() : sendSingle.mutate())}
              className="bg-[color:var(--sb-saffron,#F97316)] hover:bg-[color:var(--sb-saffron-hover,#EA580C)] text-white font-semibold"
            >
              {(sendSingle.isPending || sendBroadcast.isPending) ? <Loader2 className="h-4 w-4 animate-spin mr-1.5"/> : <Send className="h-4 w-4 mr-1.5"/>}
              {schedule.enabled ? "Schedule broadcast" : channel === "broadcast" ? "Send broadcast" : `Send ${activeChan === "sms" ? "SMS" : "WhatsApp"}`}
            </Button>
          </div>
        </div>

        {/* Recent messages */}
        <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5" data-testid="twilio-recent">
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] mb-3">Recent {activeChan}</div>
          {recent.isLoading ? (
            <div className="text-sm text-[color:var(--sg-muted)] py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5"/> Loading…</div>
          ) : (recent.data?.items || []).length === 0 ? (
            <div className="text-sm text-[color:var(--sg-muted)] py-6 text-center">No {activeChan} sent yet.</div>
          ) : (
            <div className="space-y-2 max-h-[440px] overflow-y-auto">
              {(recent.data?.items || []).map((m) => (
                <div key={m._id} className="rounded-lg border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-mono truncate text-[color:var(--sg-fg)]">{m.to}</span>
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                      m.status === "failed" ? "bg-rose-100 text-rose-800" :
                      ["queued", "sent", "delivered", "accepted"].includes(m.status) ? "bg-emerald-100 text-emerald-800" :
                      "bg-slate-100 text-slate-700"}`}>{m.status}</span>
                  </div>
                  <div className="mt-1 text-xs text-[color:var(--sg-muted)] line-clamp-2">{m.body_preview}</div>
                  {m.error && <div className="mt-1 text-[11px] text-rose-600 flex items-center gap-1"><XCircle className="h-3 w-3"/> {m.error}</div>}
                  <div className="mt-1 text-[10px] text-[color:var(--sg-muted)] font-mono">{new Date(m.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {channel === "broadcast" && (
        <div className="mt-6 rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5" data-testid="scheduled-list">
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] mb-3">Scheduled broadcasts ({(scheduledList.data?.items || []).length})</div>
          {(scheduledList.data?.items || []).length === 0 ? (
            <div className="text-sm text-[color:var(--sg-muted)] py-4 text-center">No scheduled broadcasts. Toggle "Schedule for later" above to queue one.</div>
          ) : (
          <div className="space-y-2">
            {(scheduledList.data?.items || []).map((s) => (
              <div key={s._id} className="rounded-lg border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 px-3 py-2 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[color:var(--sg-fg)]">{s.channel.toUpperCase()} · {(s.recipients || []).length} recipient(s)</div>
                  <div className="text-xs text-[color:var(--sg-muted)] line-clamp-1">{s.body}</div>
                  <div className="text-[10px] text-[color:var(--sg-muted)] font-mono mt-0.5">Fires: {new Date(s.scheduled_for).toLocaleString()}</div>
                </div>
                <button
                  data-testid={`sched-cancel-${s._id}`}
                  onClick={() => cancelSched.mutate(s._id)}
                  className="text-xs text-rose-600 hover:underline font-semibold shrink-0"
                >Cancel</button>
              </div>
            ))}
          </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
